import { createLesson, updateLesson, uploadLessonVideo } from "@/api/common";
import {
  CustomEditor,
  CustomInput,
  Dropzone,
  SubjectAndClassSelect,
} from "@/components";
import CustomSelect from "@/components/CustomSelect";
import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/useAuth";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { convertToEditorState, getFromLocalStorage } from "@/lib/utils";
import { ILesson } from "@/types/lesson.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";
import { v4 as uuidv4 } from 'uuid';

type Props = {
  closeModal: () => void;
  lesson?: ILesson;
};

const AddSingleLesson = ({ closeModal, lesson }: Props) => {
  const { currentTermId, user, school } = useAuth();
  const queryClient = useQueryClient();

  const initialValues = {
    lesson_id: lesson?.id ?? "",
    topic: lesson?.topic ?? "",
    subject_id: lesson?.subject_id ?? "",
    class_id: lesson?.class_id ?? "",
    week: lesson?.week ?? "",
    content: lesson?.content ?? "",
    attachments: null,
    type: "",
    term_id: lesson?.term_id ?? currentTermId,
    teacher_id: user?.id,
    status: lesson?.status ?? school?.options?.["course-approver"] === "staff" ? "approved" : "draft"
  };
  const validationSchema = Yup.object().shape({
    topic: Yup.string().required("This field is required"),
    week: Yup.string().required("This field is required"),
    subject_id: Yup.string().required("This field is required"),
    class_id: Yup.string().required("This field is required"),
    content: Yup.string(),
  });

  const { mutate, isLoading } = useMutation(
    !!lesson ? updateLesson : createLesson,
    {
      onSuccess: () => {
        showSuccessToast("Lesson created successfully");
        queryClient.refetchQueries({
          queryKey: ["lesson"],
          type: "active",
        });
        closeModal();
      },
    },
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={({ attachments, ...values }) => {
        const uploadName = uuidv4().replaceAll("-", "");

        if (values.type === "video") {
          uploadLessonVideo({ uploadName, media: attachments, school_code: getFromLocalStorage("code") })
            .then(() => showSuccessToast(`Your video for ${values.topic} has been uploaded.`))
            .catch(() => showErrorToast(`Your video for ${values.topic} couldn't be uploaded.`))
        }

        const data = {
          ...values,
          attachments: values.type === "file" ? attachments : "",
          fileLink: values.type === "video" ? uploadName : ""
        }

        mutate(data)
      }}
    >
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit} className="grid gap-5 pt-8">
          <CustomInput
            label="Lesson Topic"
            value={values.topic}
            onChange={handleChange}
            placeholder="Enter lesson title"
            name="topic"
          />
          <div className="grid gap-5 @[350px]/sheet:grid-cols-2">
            <SubjectAndClassSelect
              subjectValue={values.subject_id}
              subjectOnChange={(val) => setFieldValue("subject_id", val)}
              classOnChange={(val) => setFieldValue("class_id", val)}
              key={`${values.subject_id}-${values.class_id}`}
              classValue={values.class_id}
              edit
            />
            <CustomSelect
              options={Array(15)
                .fill("")
                .map((_, index) => ({
                  label: (index + 1).toString(),
                  value: (index + 1).toString(),
                }))}
              value={values.week}
              onChange={(val) => setFieldValue("week", val)}
              placeholder="Select week"
              name="week"
              label="Week"
            />
          </div>
          <CustomEditor
            label="Lesson Notes"
            setState={(editorState) => setFieldValue("content", editorState)}
            defaultEditorState={convertToEditorState(values.content)}
          />
          <Dropzone
            name="attachments"
            onDrop={(file: File) => {
              if (file.type.match("video.*")) {
                setFieldValue("type", "video");
              } else {
                setFieldValue("type", "file");
              }
            }}
          />
          <div className="flex items-center gap-3 ml-auto">
            <Button type="button" variant="outline">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              Save
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddSingleLesson;
